import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

import { FieldChrome } from './FieldChrome';

import { cn } from '@/lib/utils';

export const baseControlClasses =
  'w-full bg-secondary/60 text-foreground border border-border rounded-2xl px-4 py-3 text-base transition-all duration-300 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20 focus:border-ring';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, optional, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const invalid = error !== undefined && error !== '';
  return (
    <FieldChrome label={label} hint={hint} error={error} optional={optional} id={fieldId}>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={invalid || undefined}
        className={cn(
          baseControlClasses,
          invalid && 'border-destructive/50 focus:border-destructive focus:ring-destructive/20',
          className,
        )}
        {...rest}
      />
    </FieldChrome>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, optional, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const invalid = error !== undefined && error !== '';
  return (
    <FieldChrome label={label} hint={hint} error={error} optional={optional} id={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={invalid || undefined}
        className={cn(
          baseControlClasses,
          invalid && 'border-destructive/50 focus:border-destructive focus:ring-destructive/20',
          className,
        )}
        {...rest}
      />
    </FieldChrome>
  );
});
